import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import AdUnit from '../components/AdUnit';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

// Get site domain from environment variables
const SITE_DOMAIN = process.env.REACT_APP_SITE_DOMAIN || 'https://yourjsontools.com';

const DummyPDFPage = () => {
  // PDF options
  const pdfOptions = [
    { pages: 1, targetSize: 10, label: '1 Page', description: '~10KB file size' },
    { pages: 2, targetSize: 20, label: '2 Pages', description: '~20KB file size' },
    { pages: 5, targetSize: 50, label: '5 Pages', description: '~50KB file size' },
    { pages: 10, targetSize: 100, label: '10 Pages', description: '~100KB file size' },
    { pages: 20, targetSize: 200, label: '20 Pages', description: '~200KB file size' },
    { pages: 50, targetSize: 500, label: '50 Pages', description: '~500KB file size' }
  ];

  // Generate dummy PDF using jsPDF
  const generatePDF = (pages, targetSize) => {
    const doc = new jsPDF();
    
    for (let i = 1; i <= pages; i++) {
      if (i > 1) {
        doc.addPage();
      }
      
      // Add header
      doc.setFontSize(20);
      doc.text(`Page ${i} of ${pages}`, 20, 30);
      
      // Add subtitle
      doc.setFontSize(14);
      doc.text('Dummy PDF Generated by yourjsontools.com', 20, 45);
      
      // Add content
      doc.setFontSize(12);
      let yPosition = 65;
      
      doc.text(`This is a sample PDF document with ${pages} pages.`, 20, yPosition);
      yPosition += 10;
      doc.text(`Target size: ~${targetSize}KB`, 20, yPosition);
      yPosition += 20;
      
      // Add lorem ipsum content to fill the page
      const loremText = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.';
      
      const lines = doc.splitTextToSize(loremText, 170);
      
      // Add multiple paragraphs to fill the page
      for (let j = 0; j < 8 && yPosition < 250; j++) {
        doc.text(`Paragraph ${j + 1}:`, 20, yPosition);
        yPosition += 10;
        doc.text(lines, 20, yPosition);
        yPosition += lines.length * 5 + 10;
      }
      
      // Add footer
      doc.setFontSize(10);
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 280);
      doc.text(`yourjsontools.com - Free Developer Tools`, 20, 290);
    }
    
    // Save the PDF
    doc.save(`dummy-pdf-${pages}pages-${targetSize}KB.pdf`);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Dummy PDF Generator",
    "url": `${SITE_DOMAIN}/dummy-files/pdf`,
    "description": "Generate dummy PDF documents with various page counts and file sizes for testing purposes.",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <>
      <SEO 
        title="Dummy PDF Generator - Create Test PDF Files | yourjsontools.com"
        description="Generate dummy PDF documents with different page counts (1-50 pages) and file sizes for testing file upload systems and document processing."
        keywords={["dummy PDF", "test PDF", "sample PDF", "PDF generator", "testing tools", "development tools"]}
        canonicalUrl={`${SITE_DOMAIN}/dummy-files/pdf`}
        structuredData={jsonLd}
      />

      <div className="page-container">
        <div className="page-header">
          <nav className="breadcrumb">
            <Link to="/dummy-files">Dummy Files</Link> / <span>PDF</span>
          </nav>
          <h1>Dummy PDF Generator</h1>
          <p className="page-description">
            Generate sample PDF documents with varying page counts and file sizes for testing file upload functionality and document processing systems.
          </p>
        </div>

        <div className="tool-section">
          <div className="pdf-grid">
            {pdfOptions.map((option, index) => (
              <div key={index} className="pdf-card">
                <div className="pdf-icon">
                  📄
                </div>
                <div className="pdf-info">
                  <h3>{option.label}</h3>
                  <p>{option.description}</p>
                  <div className="pdf-details">
                    <span className="detail-item">
                      <strong>Pages:</strong> {option.pages}
                    </span>
                    <span className="detail-item">
                      <strong>Size:</strong> ~{option.targetSize}KB
                    </span>
                  </div>
                  <button
                    className="download-button"
                    onClick={() => generatePDF(option.pages, option.targetSize)}
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="info-section">
          <h2>About Dummy PDFs</h2>
          <div className="info-grid">
            <div className="info-card">
              <h3>📄 Real PDF Format</h3>
              <p>
                Generated PDFs are in proper PDF format with headers, content, and footers. 
                Perfect for testing document processing systems.
              </p>
            </div>
            <div className="info-card">
              <h3>📊 Various Sizes</h3>
              <p>
                Different page counts and file sizes to test various scenarios. 
                From small single-page documents to larger multi-page files.
              </p>
            </div>
            <div className="info-card">
              <h3>⚡ Instant Generation</h3>
              <p>
                PDFs are created instantly using jsPDF library and downloaded immediately. 
                No server processing required.
              </p>
            </div>
            <div className="info-card">
              <h3>🔒 Privacy First</h3>
              <p>
                All PDF generation happens locally in your browser. No data is sent to servers, 
                ensuring complete privacy and security.
              </p>
            </div>
          </div>
        </div>

        <div className="usage-section">
          <h2>Common Use Cases</h2>
          <div className="usage-grid">
            <div className="usage-card">
              <h3>🧪 File Upload Testing</h3>
              <p>Test file upload functionality with different file sizes and page counts.</p>
            </div>
            <div className="usage-card">
              <h3>📋 Document Processing</h3>
              <p>Test document parsing, OCR, and content extraction systems.</p>
            </div>
            <div className="usage-card">
              <h3>💾 Storage Testing</h3>
              <p>Test file storage systems and backup solutions with various file sizes.</p>
            </div>
            <div className="usage-card">
              <h3>🔄 Workflow Testing</h3>
              <p>Test document workflows, approval processes, and automation systems.</p>
            </div>
          </div>
        </div>

      </div>

      <Footer />

      <style jsx>{`
        .breadcrumb {
          margin-bottom: 1rem;
          font-size: 0.9rem;
          color: #666;
        }

        .breadcrumb a {
          color: #3498db;
          text-decoration: none;
        }

        .breadcrumb a:hover {
          text-decoration: underline;
        }

        .pdf-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .pdf-card {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          text-align: center;
        }

        .pdf-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .pdf-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .pdf-info h3 {
          margin: 0 0 0.5rem 0;
          color: #2c3e50;
          font-size: 1.3rem;
        }

        .pdf-info p {
          margin: 0 0 1rem 0;
          color: #7f8c8d;
          font-size: 0.9rem;
        }

        .pdf-details {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 6px;
        }

        .detail-item {
          font-size: 0.85rem;
          color: #555;
        }

        .download-button {
          background: #e74c3c;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          transition: background-color 0.2s ease;
          width: 100%;
        }

        .download-button:hover {
          background: #c0392b;
        }

        .info-section, .usage-section {
          margin-top: 3rem;
        }

        .info-grid, .usage-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-top: 1.5rem;
        }

        .info-card, .usage-card {
          background: white;
          padding: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .info-card h3, .usage-card h3 {
          margin: 0 0 1rem 0;
          color: #2c3e50;
        }

        .info-card p, .usage-card p {
          margin: 0;
          color: #7f8c8d;
          line-height: 1.6;
        }

        @media (max-width: 768px) {
          .pdf-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .pdf-card {
            padding: 1.5rem;
          }

          .pdf-details {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>
    </>
  );
};

export default DummyPDFPage; 